import { atom, useAtom } from 'jotai';
import { useEffect } from 'react';
import bmrsApi from '../services/bmrsApi';

// Atom to store raw BMU data from API
const bmuRawDataAtom = atom(null);

// Atom to store capacity lookup from REMIT data
const capacityDataAtom = atom({});

// Atom to track loading state
const bmuLoadingAtom = atom(false);

// Atom to track error state
const bmuErrorAtom = atom(null);

// Atom to track if data has been fetched
const bmuFetchedAtom = atom(false);

// Derived atom: transformed plant list (excludes interconnectors, with proper BMU ID format)
const bmuPlantsAtom = atom((get) => {
  const rawData = get(bmuRawDataAtom);
  const capacityData = get(capacityDataAtom);
  if (!rawData || !Array.isArray(rawData)) return [];

  // Use a Set to track seen BMU IDs and avoid duplicates
  const seenIds = new Set();

  return rawData
    .filter((bmu) => {
      // Try multiple possible field names for type
      const bmUnitType = bmu.bmUnitType || bmu.type || bmu.unitType || '';
      // Filter out interconnectors (type 'I')
      return bmUnitType !== 'I';
    })
    .map((bmu) => {
      // Try multiple possible field names
      const bmUnitType = bmu.bmUnitType || bmu.type || bmu.unitType || '';
      const baseId = bmu.nationalGridBmUnit || bmu.ngcBmUnit || bmu.bmUnit || '';
      const elexonId = bmu.elexonBmUnit || bmu.elexonBmUnitId || '';

      // Construct full BMU ID with type prefix (e.g., "T_HUMR-1", "E_ABERU-1")
      let bmuId;
      if (bmUnitType && baseId) {
        bmuId = `${bmUnitType}_${baseId}`;
      } else if (elexonId) {
        bmuId = elexonId;
      } else if (baseId) {
        bmuId = baseId;
      } else {
        bmuId = bmu.bmUnit || bmu.id || bmu.unitId || 'Unknown';
      }

      // Skip duplicates
      if (seenIds.has(bmuId)) {
        return null;
      }
      seenIds.add(bmuId);

      // Look up capacity from REMIT data (try multiple key formats)
      const remitCapacity = capacityData[bmuId] ||
        capacityData[baseId] ||
        capacityData[elexonId] ||
        0;

      // Get name - prefer descriptive name over ID
      const name = bmu.bmUnitName || bmu.name || bmu.registeredResourceName ||
        bmu.unitName || bmu.description || baseId || bmuId;

      // Parse capacity - API returns it as a string like "15.400"
      const generationCap = parseFloat(bmu.generationCapacity) || 0;
      const demandCap = parseFloat(bmu.demandCapacity) || 0;
      const capacity = remitCapacity || generationCap || demandCap ||
        bmu.installedCapacity || bmu.registeredCapacity || 0;

      return {
        bmuId,
        name,
        fuelType: bmu.fuelType || bmu.pSRType || bmu.psrType || bmu.fuel || 'OTHER',
        capacity,
        leadParty: bmu.leadPartyName || bmu.companyRegistrationNumber || bmu.leadParty || '',
      };
    })
    .filter(Boolean); // Remove null entries (duplicates)
});

// Fallback demo plants in case API fails
const FALLBACK_PLANTS = [
  { bmuId: 'T_DRAXX-1', name: 'Drax 1', fuelType: 'BIOMASS', capacity: 645 },
  { bmuId: 'T_DRAXX-2', name: 'Drax 2', fuelType: 'BIOMASS', capacity: 645 },
  { bmuId: 'T_DIDCB-6', name: 'Didcot B', fuelType: 'CCGT', capacity: 1430 },
  { bmuId: 'T_PEMB-1', name: 'Pembroke 1', fuelType: 'CCGT', capacity: 530 },
  { bmuId: 'T_SIZB-1', name: 'Sizewell B', fuelType: 'NUCLEAR', capacity: 1198 },
  { bmuId: 'T_HEYM-1', name: 'Heysham 1', fuelType: 'NUCLEAR', capacity: 600 },
  { bmuId: 'T_HUMR-1', name: 'Humber Gateway', fuelType: 'WIND', capacity: 219 },
  { bmuId: 'T_HOWBO-1', name: 'Hornsea 1', fuelType: 'WIND', capacity: 1218 },
  { bmuId: 'T_DINO-1', name: 'Dinorwig', fuelType: 'PS', capacity: 1728 },
];

/**
 * Hook to fetch and cache BMU reference data
 * Returns { plants, loading, error, refetch }
 */
export function useBMUList() {
  const [rawData, setRawData] = useAtom(bmuRawDataAtom);
  const [loading, setLoading] = useAtom(bmuLoadingAtom);
  const [error, setError] = useAtom(bmuErrorAtom);
  const [fetched, setFetched] = useAtom(bmuFetchedAtom);
  const [plants] = useAtom(bmuPlantsAtom);

  const fetchBMUs = async (force = false) => {
    // Skip if already fetched and not forcing refresh
    if (fetched && !force && rawData) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch BMU list (capacity is included as generationCapacity field)
      const bmuResponse = await bmrsApi.getBMUList();

      // Handle various response formats
      const bmuData = Array.isArray(bmuResponse)
        ? bmuResponse
        : (bmuResponse?.data || bmuResponse?.items || []);

      if (bmuData && bmuData.length > 0) {
        setRawData(bmuData);
        setFetched(true);
      } else {
        throw new Error('No BMU data in response');
      }
    } catch (err) {
      console.error('Failed to fetch BMU list:', err);
      setError('Failed to load BMU list from BMRS API. Using sample data.');
      // Don't set rawData to fallback - let components handle it
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on first use
  useEffect(() => {
    if (!fetched && !loading) {
      fetchBMUs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetched, loading]);

  // Return plants with fallback if needed
  const effectivePlants = plants.length > 0 ? plants : (error ? FALLBACK_PLANTS : []);

  return {
    plants: effectivePlants,
    loading,
    error,
    refetch: () => fetchBMUs(true),
  };
}

/**
 * Hook to get plants filtered by fuel type (uses cached BMU data)
 */
export function usePlantsByFuelType(fuelType) {
  const { plants, loading, error } = useBMUList();

  // Filter plants by fuel type
  const filteredPlants = fuelType
    ? plants.filter((p) => p.fuelType === fuelType)
    : [];

  return {
    plants: filteredPlants,
    loading,
    error,
  };
}

export {
  bmuRawDataAtom,
  bmuPlantsAtom,
  bmuLoadingAtom,
  bmuErrorAtom,
  capacityDataAtom,
};
